import { useSelector } from 'hooks/reactRedux';
import { useQuery } from 'react-query';

import { getSettingsGroup } from 'RestAPIs/admin';
import {
  getEmploymentClaYear,
  getEmploymentHours
} from 'RestAPIs/employees/employment-hours';

import { PLUS_MINUS_PRESENTATION_MODE } from '../EmploymentHours.consts';
import {
  computeTotalsRow,
  parseWabSettings,
  toGridRows
} from '../EmploymentHours.utils';

import type { ClaYearInfo, EmploymentHoursRow } from '../EmploymentHours.types';

export const employmentHoursKeys = {
  hours: (
    employeeId?: number | null,
    workAgreementId?: number | null,
    year?: number
  ) => ['employmentHours', employeeId, workAgreementId, year] as const,
  claYear: (
    employeeId?: number | null,
    workAgreementId?: number | null,
    year?: number
  ) => ['employmentClaYear', employeeId, workAgreementId, year] as const,
  wab: ['employmentHoursWabSettings'] as const
};

type UseEmploymentHoursDataParams = {
  employeeId: number | null;
  workAgreementId: number | null;
  year?: number;
};

/**
 * Loads everything the grid needs: the monthly rows, the CLA year that decides which columns exist,
 * and the WAB thresholds.
 *
 * Dojo re-fetched the WAB settings on every year change (`EmployeeWorkAgreementsHours.js:275`);
 * React Query caches them once. A WAB failure must not blank the grid — Dojo showed a message and
 * carried on — so that query's error is swallowed into `null`.
 */
export const useEmploymentHoursData = ({
  employeeId,
  workAgreementId,
  year
}: UseEmploymentHoursDataParams) => {
  'use memo';

  const enabled = Boolean(employeeId && workAgreementId && year);

  const showPlusMinus =
    useSelector(
      (state) =>
        state.organisationPreferences?.settings?.[
          'hour.management.presentation.mode'
        ]
    ) === PLUS_MINUS_PRESENTATION_MODE;

  const hoursQuery = useQuery({
    queryKey: employmentHoursKeys.hours(employeeId, workAgreementId, year),
    queryFn: () =>
      getEmploymentHours(
        employeeId as number,
        workAgreementId as number,
        year as number
      ),
    enabled
  });

  const claYearQuery = useQuery({
    queryKey: employmentHoursKeys.claYear(employeeId, workAgreementId, year),
    queryFn: () =>
      getEmploymentClaYear(
        employeeId as number,
        workAgreementId as number,
        year as number
      ),
    enabled
  });

  const wabQuery = useQuery({
    queryKey: employmentHoursKeys.wab,
    queryFn: () => getSettingsGroup('wab').then((response) => response.data),
    // the grid is still useful without WAB colouring
    onError: () => undefined
  });

  const rows: EmploymentHoursRow[] = hoursQuery.data ?? [];
  const wabSettings = wabQuery.isError ? null : parseWabSettings(wabQuery.data);
  const claYear: ClaYearInfo | undefined = claYearQuery.data;

  return {
    gridRows: toGridRows(rows, wabSettings),
    totalsRow: computeTotalsRow(rows),
    hasRows: rows.length > 0,
    claYear,
    showPlusMinus,
    isLoading: enabled && (hoursQuery.isLoading || claYearQuery.isLoading),
    isHoursError: hoursQuery.isError,
    isClaYearError: claYearQuery.isError
  };
};
